export const CONTEXT_SETTER_CONTEXT_STORAGE_KEY = 'zapp-context-setter:context';

export const CONTEXT_SETTER_REVALIDATE_STORAGE_PREFIX =
  'zapp-context-setter:revalidate';

export const CONTEXT_SETTER_READY_EVENT = 'zapp-context-setter:ready';

export const CLIENT_CONTEXT_QUERY_PARAM = 'client-context';

export type ClientContextKeys = Record<string, string | number | boolean>;

/**
 * Reads Context Setter keys stored under the single sessionStorage key.
 *
 * @returns Flat context keys, or an empty object when missing or invalid.
 */
export function getStoredContextSetterKeys(): ClientContextKeys {
  if (typeof window === 'undefined' || !window.sessionStorage) return {};

  try {
    const storedContextRaw: string | null = window.sessionStorage.getItem(
      CONTEXT_SETTER_CONTEXT_STORAGE_KEY
    );

    if (!storedContextRaw) return {};

    const parsedContext: unknown = JSON.parse(storedContextRaw);

    if (!parsedContext || typeof parsedContext !== 'object') return {};

    return Object.entries(parsedContext as Record<string, unknown>).reduce(
      (
        clientContextKeys: ClientContextKeys,
        [key, value]: [string, unknown]
      ) => {
        if (
          typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean'
        ) {
          clientContextKeys[key] = value;
        }

        return clientContextKeys;
      },
      {}
    );
  } catch {
    return {};
  }
}

/**
 * Whether Context Setter remote data is available in sessionStorage.
 *
 * @returns `true` when stored context keys exist.
 */
export function hasStoredContextSetterKeys(): boolean {
  return Object.keys(getStoredContextSetterKeys()).length > 0;
}

/**
 * Persists Context Setter keys under the single sessionStorage key.
 *
 * @param clientContextKeys - Flat context keys to store.
 * @returns Nothing.
 */
export function storeContextSetterKeys(
  clientContextKeys: ClientContextKeys
): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;

  if (Object.keys(clientContextKeys).length === 0) return;

  window.sessionStorage.setItem(
    CONTEXT_SETTER_CONTEXT_STORAGE_KEY,
    JSON.stringify(clientContextKeys)
  );
}

/**
 * Removes stored Context Setter keys from sessionStorage.
 *
 * @returns Nothing.
 */
export function clearStoredContextSetterKeys(): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;

  window.sessionStorage.removeItem(CONTEXT_SETTER_CONTEXT_STORAGE_KEY);
}

/**
 * Clears Context Setter revalidation timestamps from sessionStorage.
 *
 * @returns Nothing.
 */
export function clearRemoteContextRevalidationEntries(): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;

  for (let index = window.sessionStorage.length - 1; index >= 0; index -= 1) {
    const storageKey: string | null = window.sessionStorage.key(index);

    if (storageKey?.startsWith(CONTEXT_SETTER_REVALIDATE_STORAGE_PREFIX)) {
      window.sessionStorage.removeItem(storageKey);
    }
  }
}

/**
 * Dispatches the Context Setter ready event on `window`.
 *
 * @returns Nothing.
 */
export function dispatchContextSetterReadyEvent(): void {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(new CustomEvent(CONTEXT_SETTER_READY_EVENT));
}

/**
 * Clears cached Context Setter data so the next fetch uses the current session.
 *
 * @returns Nothing.
 */
export function invalidateStoredRemoteContext(): void {
  clearStoredContextSetterKeys();
  clearRemoteContextRevalidationEntries();
  dispatchContextSetterReadyEvent();
}
